import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import * as RGL from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { AppDispatch } from 'store'
import { getDashboardData } from 'features/dashboard/dashboardSlice'
import { getHrDashboardData } from 'features/hrDashboard/hr.slice'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { PageWrapper } from 'styling/GlobalStyles'
import {
  PageHeader,
  HeaderText,
  PageTitle,
  GridArea,
  WidgetRemove,
  PaletteOverlay,
  PaletteDrawer,
  PaletteHeader,
  PaletteTitle,
  PaletteClose,
  PaletteItem,
  PaletteItemInfo,
  PaletteItemTitle,
  PaletteItemDesc,
  PaletteAdd,
  PaletteEmpty,
} from 'pages/HrDashboard/hrLayout.styled'
import { DashGridCell, DashEditScrim } from './Dashboard.styled'
import { TasksWidget } from './Widgets/TasksWidget'
import { CyclesWidget } from './Widgets/CyclesWidget'
import { WorkersWidget } from './Widgets/WorkersWidget'
import { InventoryWidget } from './Widgets/InventoryWidget'
import { MachineryWidget } from './Widgets/MachineryWidget'
import { AlertsWidget } from './Widgets/AlertsWidget'
import { FinancesWidget } from './Widgets/FinancesWidget'
import { WorkflowsWidget } from './Widgets/WorkflowsWidget'
import { HrAlertsAndRequestsDashboardWidget } from './Widgets/HrAlertsAndRequestsDashboardWidget'
import { AttendanceWidget } from 'pages/HrDashboard/Widgets/AttendanceWidget'
import { HrShiftsWidget } from 'pages/HrDashboard/Widgets/HrShiftsWidget'
import { HrAccommodationWidget } from 'pages/HrDashboard/Widgets/HrAccommodationWidget'
import { HrNotificationWidget } from 'pages/HrDashboard/Widgets/HrNotificationsWidget'
import { HrAISuggestionsWidget } from 'pages/HrDashboard/Widgets/HrAISuggestionsWidget'
import { HrFoodOperationsWidget } from 'pages/HrDashboard/Widgets/HrFoodOperationsWidget'
import { HrTransportationWidget } from 'pages/HrDashboard/Widgets/HrTransportationWidget'
import { HrByDepartmentWidget } from 'pages/HrDashboard/Widgets/HrByDepartmentWidget'

const ResponsiveGridLayout = RGL.WidthProvider(RGL.Responsive)
const STORAGE_KEY = 'clientDashboardState'
const noop = () => undefined

const WIDGET_META = {
  tasks: { title: 'Tasks', desc: 'All task statuses', Comp: TasksWidget, w: 1, h: 6 },
  cycles: { title: 'Cycles', desc: 'All cycle statuses', Comp: CyclesWidget, w: 1, h: 6 },
  workers: { title: 'Employees', desc: 'Workers by shifts', Comp: WorkersWidget, w: 1, h: 6 },
  inventory: { title: 'Inventory', desc: 'Selected items count', Comp: InventoryWidget, w: 1, h: 6 },
  machinery: { title: 'Machinery', desc: 'Machinery statuses', Comp: MachineryWidget, w: 1, h: 6 },
  alerts: { title: 'Alerts', desc: 'Current alerts', Comp: AlertsWidget, w: 2, h: 7 },
  finances: { title: 'Finances', desc: 'Finances for present day', Comp: FinancesWidget, w: 1, h: 6 },
  workflows: { title: 'Workflows', desc: 'All workflows statuses', Comp: WorkflowsWidget, w: 1, h: 6 },
  attendance: { title: 'Attendance', desc: 'Attendance overview', Comp: AttendanceWidget, w: 1, h: 6 },
  shifts: { title: 'Shifts', desc: 'Shift coverage', Comp: HrShiftsWidget, w: 1, h: 6 },
  accommodation: { title: 'Accommodation', desc: 'Accommodation status', Comp: HrAccommodationWidget, w: 1, h: 6 },
  notifications: { title: 'Notifications', desc: 'Latest HR notifications', Comp: HrNotificationWidget, w: 1, h: 6 },
  suggestions: { title: 'AI Suggestions', desc: 'Latest AI suggestions', Comp: HrAISuggestionsWidget, w: 2, h: 6 },
  'hr-alerts': { title: 'Alerts & Requests', desc: 'HR alerts and requests', Comp: HrAlertsAndRequestsDashboardWidget, w: 2, h: 6 },
  food: { title: 'Food Operations', desc: 'Food operations status', Comp: HrFoodOperationsWidget, w: 1, h: 6 },
  transportation: { title: 'Transportation', desc: 'Transportation status', Comp: HrTransportationWidget, w: 1, h: 6 },
  department: { title: 'By Department', desc: 'Department overview', Comp: HrByDepartmentWidget, w: 1, h: 6 },
}

const ALL_KEYS = Object.keys(WIDGET_META)
const DEFAULT_PRESENT = ['alerts', 'tasks', 'cycles', 'finances', 'workers', 'machinery', 'inventory']

const DEFAULT_LAYOUT: RGL.Layout[] = [
  { i: 'alerts', x: 0, y: 0, w: 2, h: 7, minW: 1, minH: 5 },
  { i: 'tasks', x: 2, y: 0, w: 1, h: 6, minW: 1, minH: 5 },
  { i: 'cycles', x: 3, y: 0, w: 1, h: 6, minW: 1, minH: 5 },
  { i: 'finances', x: 0, y: 7, w: 1, h: 6, minW: 1, minH: 5 },
  { i: 'workers', x: 1, y: 7, w: 1, h: 6, minW: 1, minH: 5 },
  { i: 'machinery', x: 2, y: 7, w: 1, h: 6, minW: 1, minH: 5 },
  { i: 'inventory', x: 3, y: 7, w: 1, h: 6, minW: 1, minH: 5 },
]

const normalizeLayout = (layout: RGL.Layout[]) =>
  layout.map((item) => {
    const targetH = item.i === 'alerts' ? 7 : 6
    return {
      ...item,
      h: Math.max(item.h ?? 4, targetH),
      minH: Math.max(item.minH ?? 3, 5),
    }
  })

const loadState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed.present) && Array.isArray(parsed.layout) && parsed.present.length) {
        return {
          present: parsed.present as string[],
          layout: normalizeLayout(parsed.layout as RGL.Layout[]),
        }
      }
    }
  } catch {
    return { present: DEFAULT_PRESENT, layout: DEFAULT_LAYOUT }
  }
  return { present: DEFAULT_PRESENT, layout: DEFAULT_LAYOUT }
}

export const ClientDashboard = () => {
  const dispatch = useDispatch<AppDispatch>()
  const [editMode, setEditMode] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [present, setPresent] = useState<string[]>(() => loadState().present)
  const [layout, setLayout] = useState<RGL.Layout[]>(() => loadState().layout)

  useEffect(() => {
    dispatch(getDashboardData())
    dispatch(getHrDashboardData())
  }, [])

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (!saved) return
      const parsed = JSON.parse(saved)
      if (!Array.isArray(parsed.layout)) return
      const normalized = normalizeLayout(parsed.layout as RGL.Layout[])
      if (JSON.stringify(normalized) !== JSON.stringify(parsed.layout)) {
        persist(parsed.present ?? present, normalized)
        setLayout(normalized)
      }
    } catch {
      return
    }
  }, [])

  const persist = (nextPresent: string[], nextLayout: RGL.Layout[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ present: nextPresent, layout: nextLayout }))
  }

  const handleLayoutChange = (current: RGL.Layout[]) => {
    if (!editMode) return
    setLayout(current)
    persist(present, current)
  }

  const addWidget = (key: string) => {
    if (present.includes(key)) return
    const meta = WIDGET_META[key]
    const nextPresent = [...present, key]
    const nextLayout = [...layout, { i: key, x: 0, y: Infinity, w: meta.w, h: meta.h, minW: 1, minH: 5 }]
    setPresent(nextPresent)
    setLayout(nextLayout)
    persist(nextPresent, nextLayout)
  }

  const removeWidget = (key: string) => {
    const nextPresent = present.filter((k) => k !== key)
    const nextLayout = layout.filter((l) => l.i !== key)
    setPresent(nextPresent)
    setLayout(nextLayout)
    persist(nextPresent, nextLayout)
  }

  const available = ALL_KEYS.filter((k) => !present.includes(k))

  const renderWidget = (key: string) => {
    const Comp = WIDGET_META[key].Comp
    return (
      <DashGridCell>
        <Comp modalOpen={false} removeWidget={noop} onDragOver={noop} onDragStart={noop} onDragDrop={noop} />
        {editMode && <DashEditScrim />}
        {editMode && (
          <WidgetRemove
            className='dash-no-drag'
            style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 4 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              removeWidget(key)
            }}
            aria-label={'Remove ' + WIDGET_META[key].title}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M6 6l12 12M18 6L6 18' />
            </svg>
          </WidgetRemove>
        )}
      </DashGridCell>
    )
  }

  return (
    <>
      <NavBar input placeholder='Search…' setParams={() => undefined} params={{}} />
      <PageWrapper noFooter>
        <PageHeader>
          <HeaderText>
            <PageTitle>Dashboard</PageTitle>
          </HeaderText>
          {editMode && (
            <Button variant='secondary' onClick={() => setPaletteOpen(true)}>
              + Add widget
            </Button>
          )}
          <Button variant='primary' onClick={() => setEditMode((value) => !value)}>
            {editMode ? 'Done' : 'Edit widgets'}
          </Button>
        </PageHeader>

        <GridArea $editing={editMode}>
          <ResponsiveGridLayout
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1100, md: 800, sm: 0 }}
            cols={{ lg: 4, md: 2, sm: 1 }}
            rowHeight={40}
            margin={[12, 12]}
            isDraggable={editMode}
            isResizable={editMode}
            resizeHandles={['se', 's', 'e']}
            onLayoutChange={handleLayoutChange}
            draggableCancel='button, a, .dash-no-drag'
            useCSSTransforms
          >
            {present.map((key) => (
              <div key={key}>{renderWidget(key)}</div>
            ))}
          </ResponsiveGridLayout>
        </GridArea>

        {paletteOpen && (
          <PaletteOverlay onClick={() => setPaletteOpen(false)}>
            <PaletteDrawer onClick={(e) => e.stopPropagation()}>
              <PaletteHeader>
                <PaletteTitle>Add widget</PaletteTitle>
                <PaletteClose onClick={() => setPaletteOpen(false)} aria-label='Close'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                    <path d='M6 6l12 12M18 6L6 18' />
                  </svg>
                </PaletteClose>
              </PaletteHeader>
              {available.length === 0 ? (
                <PaletteEmpty>All widgets are on your dashboard.</PaletteEmpty>
              ) : (
                available.map((key) => (
                  <PaletteItem key={key}>
                    <PaletteItemInfo>
                      <PaletteItemTitle>{WIDGET_META[key].title}</PaletteItemTitle>
                      <PaletteItemDesc>{WIDGET_META[key].desc}</PaletteItemDesc>
                    </PaletteItemInfo>
                    <PaletteAdd onClick={() => addWidget(key)}>+ Add</PaletteAdd>
                  </PaletteItem>
                ))
              )}
            </PaletteDrawer>
          </PaletteOverlay>
        )}
      </PageWrapper>
    </>
  )
}
